'use client';

import type { UpsertSurfaceProfileDto } from '@terra/shared-contracts-external-network';
import { BusinessProfileContent, toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  useExternalNetworkProfile,
  useUpsertExternalNetworkProfile,
} from '../../../../hooks/api/useSurfaceSettings';
import { usePermissions } from '../../../../hooks/usePermissions';

export function ExternalBusinessProfileSettingsPanel({
  surfaceId,
}: {
  surfaceId: string;
}) {
  const t = useTranslations('externalNetwork.settings');
  const { canManageApplications } = usePermissions();
  const {
    data: profile,
    isLoading,
    isError,
    refetch,
  } = useExternalNetworkProfile(surfaceId);
  const upsertProfile = useUpsertExternalNetworkProfile(surfaceId);

  const saveProfile = async (body: UpsertSurfaceProfileDto) => {
    try {
      await upsertProfile.mutateAsync(body);
      toasterApi.success({ title: t('saveSuccess') });

      return true;
    } catch (error: unknown) {
      console.error(
        'Failed to save external network business profile:',
        error instanceof Error ? error : new Error(String(error)),
      );
      toasterApi.error({
        title: t('saveError'),
        description:
          error instanceof Error && error.message ? error.message : undefined,
      });

      return false;
    }
  };

  return (
    <BusinessProfileContent
      profile={profile}
      isLoading={isLoading}
      isError={isError}
      isSaving={upsertProfile.isPending}
      isReadOnly={!canManageApplications}
      onRetry={() => void refetch()}
      onSave={saveProfile}
    />
  );
}
